import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';

export const ScribeViewMode = {
  MY_CONSULTATIONS: 'myConsultations',
  ALL_CONSULTATIONS: 'allConsultations',
  ARCHIVE: 'archive',
};

const RECORDING = ConsultationStatusEnum.RECORDING.value;
const PENDING = ConsultationStatusEnum.PENDING.value;
const GENERATING = ConsultationStatusEnum.GENERATING.value;
const READY = ConsultationStatusEnum.READY.value;
const FILED_AWAY = ConsultationStatusEnum.FILED_AWAY.value;

/**
 * My Consultations is the recording clinician, not a team. Null means the signed-in
 * user is not known yet, so a row already loaded from the server is left in place.
 *
 * @param {object|null|undefined} consultation
 * @param {string} viewMode
 * @param {string|null|undefined} userId
 * @returns {boolean|null}
 */
export function consultationBelongsInView(consultation, viewMode, userId) {
  if (!consultation) {
    return false;
  }

  const filedAway = consultation.status === FILED_AWAY;
  switch (viewMode) {
    case ScribeViewMode.ALL_CONSULTATIONS:
      return !filedAway;
    case ScribeViewMode.ARCHIVE:
      return filedAway;
    case ScribeViewMode.MY_CONSULTATIONS:
    default:
      return belongsToMe(consultation, filedAway, userId);
  }
}

function belongsToMe(consultation, filedAway, userId) {
  if (filedAway || !consultation.userId) {
    return false;
  }
  if (!userId) {
    return null;
  }
  return consultation.userId === userId;
}

/**
 * @param {boolean} isTracked
 * @param {{ from?: string, to?: string, type?: string, userId?: string, progress?: number }|null|undefined} transition
 * @param {string} viewMode
 * @param {string|null|undefined} userId
 * @returns {boolean}
 */
export function shouldFetchConsultationUpdate(isTracked, transition, viewMode, userId) {
  return isTracked || transitionMayEnterConsultationView(transition, viewMode, userId);
}

/**
 * A consultation the list has not seen. The clinician is fixed at creation, so My
 * Consultations gains a row only when this user starts a recording. All Consultations
 * gains any new recording. Archive gains a filing.
 *
 * @param {{ from?: string, to?: string, userId?: string }|null|undefined} transition
 * @param {string} viewMode
 * @param {string|null|undefined} userId
 * @returns {boolean}
 */
export function transitionMayEnterConsultationView(transition, viewMode, userId) {
  if (!transition || transition.to === 'NON_EXISTENT') {
    return false;
  }

  const { from, to } = transition;
  const started = from === 'NON_EXISTENT' && to === RECORDING;

  switch (viewMode) {
    case ScribeViewMode.ALL_CONSULTATIONS:
      return started;
    case ScribeViewMode.ARCHIVE:
      return to === FILED_AWAY;
    case ScribeViewMode.MY_CONSULTATIONS:
    default:
      return started && !!userId && transition.userId === userId;
  }
}

/**
 * Consultation search matches an NHS number or a patient name. Rules such as
 * created-by are rejected. A new recording already has an NHS number, so it can
 * enter an NHS search immediately. The name matches once the owning patient is set.
 *
 * @param {boolean} isInResults
 * @param {{ from?: string, to?: string, type?: string, progress?: number }|null|undefined} transition
 * @returns {boolean}
 */
export function shouldRefreshConsultationSearch(isInResults, transition) {
  if (!transition || transition.to === 'NON_EXISTENT') {
    return false;
  }
  return isInResults || transitionMayChangeConsultationSearch(transition);
}

/**
 * @param {{ from?: string, to?: string, type?: string, progress?: number }|null|undefined} transition
 * @returns {boolean}
 */
export function transitionMayChangeConsultationSearch(transition) {
  if (!transition || transition.to === 'NON_EXISTENT') {
    return false;
  }

  const { from, to, type } = transition;
  const progress = Number(transition.progress);
  const processingFinished = from === GENERATING
    && (to === READY || to === 'SKIPPED')
    && (type === 'ALL' || progress >= 1);

  return (from === 'NON_EXISTENT' && to === RECORDING)
    || (from === READY && to === PENDING)
    || type === 'NHS_ID'
    || type === 'OWNING_PATIENT'
    || processingFinished;
}

/**
 * @param {object[]|null|undefined} previous
 * @param {string} consultationId
 * @param {object|null|undefined} fetched
 * @param {boolean|null} belongs
 * @returns {object[]}
 */
export function mergeFetchedConsultation(previous, consultationId, fetched, belongs) {
  if (!Array.isArray(previous)) {
    return previous;
  }

  const position = previous.findIndex(item => item.consultationId === consultationId);
  const without = () => previous.filter(item => item.consultationId !== consultationId);

  if (fetched == null || belongs === false) {
    return position === -1 ? previous : without();
  }

  if (belongs == null) {
    if (position === -1) {
      return previous;
    }
    const next = previous.slice();
    next[position] = fetched;
    return next;
  }

  if (position === -1) {
    return [...previous, fetched];
  }

  const next = previous.slice();
  next[position] = fetched;
  return next;
}
